document.documentElement.classList.remove("no-js");
document.documentElement.classList.add("js");

const liveRegion = document.querySelector("[data-live-region]");

const announce = (message) => {
  if (!liveRegion) return;
  liveRegion.textContent = "";
  window.setTimeout(() => {
    liveRegion.textContent = message;
  }, 30);
};

/* Vitrina 3.9: mirrors Liquid `money` (shop format ${{amount}} = two decimals always).
   { trim: true } mirrors money_without_trailing_zeros for whole amounts. Cents are
   never dropped — 12549 renders $125.49, not $125. */
const formatMoney = (cents, { trim = false } = {}) => {
  const currency = window.JaneFindsTheme?.currency || "USD";
  const amount = Number(cents) / 100;
  const whole = trim && Number.isFinite(amount) && Math.round(Number(cents)) % 100 === 0;
  return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amount);
};

const progress = document.querySelector("[data-scroll-progress]");
if (progress) {
  const updateProgress = () => {
    const available = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = available > 0 ? window.scrollY / available : 0;
    progress.style.transform = `scaleX(${Math.min(Math.max(ratio, 0), 1)})`;
  };
  updateProgress();
  document.addEventListener("scroll", updateProgress, { passive: true });
}

document.querySelectorAll("[data-header]").forEach((header) => {
  const open = header.querySelector("[data-menu-open]");
  const dialog = open ? document.getElementById(open.getAttribute("aria-controls")) : null;
  const close = dialog?.querySelector("[data-menu-close]");
  if (!(dialog instanceof HTMLDialogElement) || !open || !close) return;

  open.addEventListener("click", () => {
    dialog.showModal();
    open.setAttribute("aria-expanded", "true");
    document.body.classList.add("menu-open");
  });

  const closeDialog = () => {
    if (dialog.open) dialog.close();
    open.setAttribute("aria-expanded", "false");
    document.body.classList.remove("menu-open");
    open.focus();
  };

  close.addEventListener("click", closeDialog);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) closeDialog();
  });
  dialog.addEventListener("close", () => {
    open.setAttribute("aria-expanded", "false");
    document.body.classList.remove("menu-open");
  });
});

document.querySelectorAll("[data-predictive-search-dialog]").forEach((dialog) => {
  if (!(dialog instanceof HTMLDialogElement)) return;

  const input = dialog.querySelector("[data-predictive-search-input]");
  const results = dialog.querySelector("[data-predictive-search-results]");
  const close = dialog.querySelector("[data-search-close]");
  const endpoint = dialog.dataset.url;
  const initialResults = results?.innerHTML || "";
  let request;
  let debounceTimer;
  let lastTrigger;

  const closeSearch = () => {
    request?.abort();
    if (dialog.open) dialog.close();
    document.body.classList.remove("search-open");
    document.querySelectorAll("[data-search-open]").forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
    });
    input?.setAttribute("aria-expanded", "false");
    if (lastTrigger instanceof HTMLElement && !lastTrigger.closest("dialog")) lastTrigger.focus();
  };

  const openSearch = (trigger) => {
    document.querySelectorAll("[data-mobile-menu][open]").forEach((menu) => menu.close());
    document.body.classList.remove("menu-open");
    lastTrigger = trigger;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add("search-open");
    trigger?.setAttribute("aria-expanded", "true");
    window.setTimeout(() => input?.focus(), 40);
  };

  document.querySelectorAll("[data-search-open]").forEach((trigger) => {
    const controls = trigger.getAttribute("aria-controls");
    if (controls && controls !== dialog.id) return;
    trigger.addEventListener("click", () => openSearch(trigger));
  });

  close?.addEventListener("click", closeSearch);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) closeSearch();
  });
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeSearch();
  });

  const search = async () => {
    const term = input?.value.trim() || "";
    if (!results || !input || !endpoint) return;
    if (!term) {
      request?.abort();
      results.innerHTML = initialResults;
      results.classList.remove("is-loading");
      input.setAttribute("aria-expanded", "false");
      return;
    }

    request?.abort();
    request = new AbortController();
    results.classList.add("is-loading");

    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set("q", term);
    url.searchParams.set("resources[type]", "product,collection,page,article,query");
    url.searchParams.set("resources[limit]", "6");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("section_id", "predictive-search");

    try {
      const response = await fetch(url.toString(), {
        signal: request.signal,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!response.ok) throw new Error("Predictive search unavailable");
      const html = new DOMParser().parseFromString(await response.text(), "text/html");
      const content = html.querySelector("[data-predictive-search-content]");
      results.innerHTML = content ? content.outerHTML : initialResults;
      input.setAttribute("aria-expanded", content ? "true" : "false");
      announce(content ? `Search suggestions updated for ${term}.` : `No suggestions for ${term}.`);
    } catch (error) {
      if (error.name !== "AbortError") {
        results.innerHTML = `<div class="jf-predictive__empty"><p class="jf-eyebrow">Search is momentarily unavailable</p><p>Press Enter to view full results.</p></div>`;
      }
    } finally {
      results.classList.remove("is-loading");
    }
  };

  input?.addEventListener("input", () => {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(search, 180);
  });
});

document.querySelectorAll("[data-hero-specification]").forEach((section) => {
  const specification = section.querySelector(".jf-specification");
  const buttons = [...section.querySelectorAll("[data-spec-index]")];
  const counter = section.querySelector("[data-spec-counter]");
  const total = buttons.length.toString().padStart(2, "0");

  const activate = (button) => {
    const index = Number(button.dataset.specIndex || 0);
    specification?.style.setProperty("--spec-index", index);
    buttons.forEach((item) => item.classList.toggle("is-active", item === button));
    if (counter) counter.textContent = `${String(index + 1).padStart(2, "0")} / ${total}`;
  };

  buttons.forEach((button) => {
    button.addEventListener("mouseenter", () => activate(button));
    button.addEventListener("focus", () => activate(button));
  });
});

document.querySelectorAll("[data-instinct-browser]").forEach((browser) => {
  const tabs = [...browser.querySelectorAll("[data-instinct-tab]")];
  const panels = [...browser.querySelectorAll("[data-instinct-panel]")];
  const note = browser.querySelector("[data-instinct-note]");

  const activate = (tab) => {
    const id = tab.dataset.instinctTab;
    tabs.forEach((item) => {
      const active = item === tab;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-selected", active ? "true" : "false");
      item.tabIndex = active ? 0 : -1;
    });
    panels.forEach((panel) => {
      const active = panel.dataset.instinctPanel === id;
      panel.classList.toggle("is-hidden", !active);
      panel.hidden = !active;
    });
    const currentPanel = panels.find((panel) => panel.dataset.instinctPanel === id);
    const currentNote = currentPanel?.querySelector("figcaption span")?.textContent;
    if (note && currentNote) note.textContent = currentNote;
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      if (tab.classList.contains("is-active") && tab.dataset.instinctUrl) {
        window.location.assign(tab.dataset.instinctUrl);
        return;
      }
      activate(tab);
    });
    tab.addEventListener("mouseenter", () => activate(tab));
    tab.addEventListener("focus", () => activate(tab));
    tab.addEventListener("keydown", (event) => {
      const currentIndex = tabs.indexOf(tab);
      const direction = event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
      if (!direction && event.key !== "Home" && event.key !== "End") return;
      event.preventDefault();
      const nextIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : (currentIndex + direction + tabs.length) % tabs.length;
      tabs[nextIndex]?.focus();
    });
  });
});

document.querySelectorAll("[data-product-rail]").forEach((rail) => {
  const id = rail.dataset.productRail;
  const previous = document.querySelector(`[data-rail-previous="${id}"]`);
  const next = document.querySelector(`[data-rail-next="${id}"]`);
  const move = (direction) => {
    rail.scrollBy({
      left: direction * Math.min(window.innerWidth * 0.72, 580),
      behavior: "smooth",
    });
  };
  previous?.addEventListener("click", () => move(-1));
  next?.addEventListener("click", () => move(1));
});

document.querySelectorAll("[data-collection-tabs]").forEach((section) => {
  const tabs = [...section.querySelectorAll("[data-collection-tab]")];
  const panels = [...section.querySelectorAll("[data-collection-panel]")];
  const previous = section.querySelector("[data-collection-previous]");
  const next = section.querySelector("[data-collection-next]");

  const activate = (tab) => {
    const id = tab.dataset.collectionTab;
    tabs.forEach((item) => {
      const active = item === tab;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-selected", active ? "true" : "false");
      item.tabIndex = active ? 0 : -1;
    });
    panels.forEach((panel) => {
      const active = panel.dataset.collectionPanel === id;
      panel.classList.toggle("is-hidden", !active);
      panel.hidden = !active;
    });
    const activePanel = panels.find((panel) => panel.dataset.collectionPanel === id);
    activePanel?.querySelector("[data-tabbed-product-rail]")?.scrollTo({ left: 0, behavior: "auto" });
  };

  const move = (direction) => {
    const activePanel = panels.find((panel) => !panel.classList.contains("is-hidden"));
    const rail = activePanel?.querySelector("[data-tabbed-product-rail]");
    rail?.scrollBy({
      left: direction * Math.min(window.innerWidth * 0.72, 580),
      behavior: "smooth",
    });
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => activate(tab));
    tab.addEventListener("keydown", (event) => {
      const currentIndex = tabs.indexOf(tab);
      const direction = event.key === "ArrowRight"
        ? 1
        : event.key === "ArrowLeft"
          ? -1
          : 0;
      if (!direction && event.key !== "Home" && event.key !== "End") return;
      event.preventDefault();
      const nextIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : (currentIndex + direction + tabs.length) % tabs.length;
      tabs[nextIndex]?.focus();
      activate(tabs[nextIndex]);
    });
  });
  previous?.addEventListener("click", () => move(-1));
  next?.addEventListener("click", () => move(1));
});

const initializeHorizontalRail = (rail) => {
  if (rail.dataset.horizontalRailReady === "true") return;
  rail.dataset.horizontalRailReady = "true";
  let startX = 0;
  let startScroll = 0;
  let activePointer = null;
  let dragged = false;
  let suppressClick = false;

  const finishDrag = ({ cancelled = false } = {}) => {
    const completedDrag = dragged;
    if (activePointer !== null && rail.hasPointerCapture?.(activePointer)) {
      rail.releasePointerCapture(activePointer);
    }
    activePointer = null;
    dragged = false;
    rail.classList.remove("is-dragging");
    suppressClick = !cancelled && completedDrag;
    if (suppressClick) {
      window.setTimeout(() => {
        suppressClick = false;
      }, 0);
    }
  };

  rail.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "touch" || event.button !== 0) return;
    activePointer = event.pointerId;
    startX = event.clientX;
    startScroll = rail.scrollLeft;
    dragged = false;
    suppressClick = false;
  });

  rail.addEventListener("pointermove", (event) => {
    if (activePointer !== event.pointerId) return;
    const distance = event.clientX - startX;
    if (!dragged && Math.abs(distance) > 6) {
      dragged = true;
      rail.setPointerCapture?.(event.pointerId);
      rail.classList.add("is-dragging");
    }
    if (!dragged) return;
    rail.scrollLeft = startScroll - distance;
    event.preventDefault();
  });

  rail.addEventListener("pointerup", () => finishDrag());
  rail.addEventListener("pointercancel", () => finishDrag({ cancelled: true }));
  rail.addEventListener("lostpointercapture", () => {
    if (activePointer !== null) finishDrag({ cancelled: true });
  });
  rail.addEventListener("click", (event) => {
    if (!suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClick = false;
  }, true);
};

document.querySelectorAll("[data-horizontal-rail]").forEach(initializeHorizontalRail);

document.querySelectorAll("[data-localization-select]").forEach((select) => {
  select.addEventListener("change", () => {
    if (!select.form) return;
    if (typeof select.form.requestSubmit === "function") select.form.requestSubmit();
    else select.form.submit();
  });
});

const wishlistKey = "janefinds-wishlist";
const legacySavedKey = "janefinds-private-list";
let wishlistMemory = [];

const cleanWishlist = (items) => {
  if (!Array.isArray(items)) return [];
  return [...new Set(items.filter((item) => typeof item === "string" && item.trim()).map((item) => item.trim()))];
};

const readWishlist = () => {
  try {
    let stored = window.localStorage.getItem(wishlistKey);
    if (stored === null) {
      stored = window.localStorage.getItem(legacySavedKey);
      if (stored !== null) window.localStorage.setItem(wishlistKey, stored);
    }
    wishlistMemory = cleanWishlist(JSON.parse(stored || "[]"));
  } catch {
    wishlistMemory = cleanWishlist(wishlistMemory);
  }
  return [...wishlistMemory];
};

const writeWishlist = (items) => {
  wishlistMemory = cleanWishlist(items);
  try {
    window.localStorage.setItem(wishlistKey, JSON.stringify(wishlistMemory));
  } catch {
    // The in-memory list keeps the interaction usable when browser storage is unavailable.
  }
  document.dispatchEvent(new CustomEvent("janefinds:wishlist-change", {
    detail: { handles: [...wishlistMemory] },
  }));
};

const syncWishlistControls = () => {
  const handles = readWishlist();
  document.querySelectorAll("[data-save-product]").forEach((button) => {
    const saved = handles.includes(button.dataset.saveProduct);
    const productButton = button.classList.contains("jf-product-wishlist");
    button.classList.toggle("is-saved", saved);
    button.setAttribute("aria-pressed", saved ? "true" : "false");
    button.textContent = productButton
      ? (saved ? window.JaneFindsTheme.wishlistSavedTo : window.JaneFindsTheme.wishlistSaveTo)
      : (saved ? window.JaneFindsTheme.wishlistSaved : window.JaneFindsTheme.wishlistSave);
  });
  document.querySelectorAll("[data-wishlist-count]").forEach((count) => {
    count.textContent = `(${handles.length})`;
    count.hidden = handles.length === 0;
  });
  document.querySelectorAll("[data-wishlist-heading-count]").forEach((count) => {
    count.textContent = handles.length;
  });
};

// Vitrina 3.3: saved handles persist; price status is always fetched from Liquid.
document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-save-product]");
  if (!button) return;
  const handle = button.dataset.saveProduct;
  if (!handle) return;
  const handles = readWishlist();
  const removing = handles.includes(handle);
  writeWishlist(removing
    ? handles.filter((item) => item !== handle)
    : [...handles, handle]);
  announce(removing
    ? window.JaneFindsTheme.wishlistRemovedAnnouncement
    : window.JaneFindsTheme.wishlistSavedAnnouncement);
});

window.addEventListener("storage", (event) => {
  if (event.key !== wishlistKey && event.key !== legacySavedKey) return;
  syncWishlistControls();
  document.dispatchEvent(new CustomEvent("janefinds:wishlist-change", {
    detail: { handles: readWishlist() },
  }));
});

const escapeHtml = (value = "") => String(value).replace(/[&<>"']/g, (character) => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;",
}[character]));

const shopRoot = `${window.JaneFindsTheme?.rootUrl || "/"}`.replace(/\/?$/, "/");
const loadWishlistProduct = async (handle) => {
  try {
    const response = await fetch(`${shopRoot}products/${encodeURIComponent(handle)}?section_id=wishlist-product`, {
      cache: "no-store",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (!response.ok) return null;
    const html = new DOMParser().parseFromString(await response.text(), "text/html");
    const data = html.querySelector("[data-wishlist-product]");
    if (!data) return null;
    const product = JSON.parse(data.textContent);
    if (product.handle !== handle || !["public", "inquiry", "archive"].includes(product.price_mode)) return null;
    return product;
  } catch {
    // Fail closed: never fall back to .js prices or localStorage classification.
    return null;
  }
};

const emptyWishlistMarkup = `
  <div class="jf-wishlist-empty">
    <p>${escapeHtml(window.JaneFindsTheme.wishlistEmpty)}</p>
    <span>${escapeHtml(window.JaneFindsTheme.wishlistEmptyNote)}</span>
  </div>
`;

document.querySelectorAll("[data-wishlist-dialog]").forEach((dialog) => {
  if (!(dialog instanceof HTMLDialogElement)) return;
  const itemsContainer = dialog.querySelector("[data-wishlist-items]");
  const close = dialog.querySelector("[data-wishlist-close]");
  const clear = dialog.querySelector("[data-wishlist-clear]");
  const headingCount = dialog.querySelector("[data-wishlist-heading-count]");
  let lastTrigger;
  let renderId = 0;

  const closeWishlist = () => {
    if (dialog.open) dialog.close();
    document.body.classList.remove("wishlist-open");
    document.querySelectorAll("[data-wishlist-open]").forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
    });
    if (lastTrigger instanceof HTMLElement && !lastTrigger.closest("dialog")) lastTrigger.focus();
  };

  const renderWishlist = async () => {
    if (!itemsContainer) return;
    const currentRender = ++renderId;
    const handles = readWishlist();
    if (headingCount) headingCount.textContent = handles.length;
    if (clear) clear.hidden = handles.length === 0;

    if (!handles.length) {
      itemsContainer.innerHTML = emptyWishlistMarkup;
      return;
    }

    itemsContainer.innerHTML = `
      <div class="jf-wishlist-loading" role="status">
        <span>${escapeHtml(window.JaneFindsTheme.wishlistLoading)}</span>
      </div>
    `;

    const products = (await Promise.all(handles.map(loadWishlistProduct))).filter(Boolean);
    if (currentRender !== renderId) return;

    if (!products.length) {
      itemsContainer.innerHTML = `
        <div class="jf-wishlist-empty">
          <p>${escapeHtml(window.JaneFindsTheme.wishlistUnavailable)}</p>
          <span>${escapeHtml(window.JaneFindsTheme.wishlistUnavailableNote)}</span>
        </div>
      `;
      return;
    }

    itemsContainer.innerHTML = products.map((product) => {
      const productUrl = `${shopRoot}products/${encodeURIComponent(product.handle)}`;
      const image = product.featured_image
        ? `<img src="${escapeHtml(product.featured_image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="360" height="450">`
        : `<span class="jf-wishlist-item__placeholder" aria-hidden="true">JF</span>`;
      const vendor = product.vendor ? `<small>${escapeHtml(product.vendor)}</small>` : "";
      const availability = (product.available || product.price_mode !== "public")
        ? ""
        : `<small>${escapeHtml(window.JaneFindsTheme.wishlistCurrentlyUnavailable)}</small>`;
      return `
        <article class="jf-wishlist-item">
          <a class="jf-wishlist-item__image" href="${productUrl}">
            ${image}
          </a>
          <div class="jf-wishlist-item__details">
            ${vendor}
            <h3><a href="${productUrl}">${escapeHtml(product.title)}</a></h3>
            <span class="jf-wishlist-item__price">${escapeHtml(product.price_label || "")}</span>
            ${availability}
            <div class="jf-wishlist-item__actions">
              <a href="${productUrl}">${escapeHtml(window.JaneFindsTheme.wishlistViewPiece)}</a>
              <button type="button" data-wishlist-remove="${escapeHtml(product.handle)}">${escapeHtml(window.JaneFindsTheme.wishlistRemove)}</button>
            </div>
          </div>
        </article>
      `;
    }).join("");
  };

  document.querySelectorAll("[data-wishlist-open]").forEach((trigger) => {
    const controls = trigger.getAttribute("aria-controls");
    if (controls && controls !== dialog.id) return;
    trigger.addEventListener("click", () => {
      document.querySelectorAll("[data-mobile-menu][open]").forEach((menu) => menu.close());
      document.body.classList.remove("menu-open");
      lastTrigger = trigger;
      if (!dialog.open) dialog.showModal();
      document.body.classList.add("wishlist-open");
      trigger.setAttribute("aria-expanded", "true");
      renderWishlist();
    });
  });

  close?.addEventListener("click", closeWishlist);
  clear?.addEventListener("click", () => {
    writeWishlist([]);
    announce(window.JaneFindsTheme.wishlistClearedAnnouncement);
  });
  itemsContainer?.addEventListener("click", (event) => {
    const remove = event.target.closest("[data-wishlist-remove]");
    if (!remove) return;
    const handle = remove.dataset.wishlistRemove;
    writeWishlist(readWishlist().filter((item) => item !== handle));
    announce(window.JaneFindsTheme.wishlistRemovedAnnouncement);
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) closeWishlist();
  });
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeWishlist();
  });
  dialog.addEventListener("close", () => {
    document.body.classList.remove("wishlist-open");
    document.querySelectorAll("[data-wishlist-open]").forEach((trigger) => {
      trigger.setAttribute("aria-expanded", "false");
    });
  });
  document.addEventListener("janefinds:wishlist-change", () => {
    syncWishlistControls();
    if (dialog.open) renderWishlist();
  });
});

syncWishlistControls();

document.querySelectorAll("[data-eye-inspector]").forEach((inspector) => {
  const markers = [...inspector.querySelectorAll("[data-eye-marker]")];
  const tabs = [...inspector.querySelectorAll("[data-eye-tab]")];
  const displays = [...inspector.querySelectorAll("[data-eye-display]")];
  const counter = inspector.querySelector("[data-eye-counter]");

  const activate = (id) => {
    markers.forEach((marker, index) => {
      const active = marker.dataset.eyeMarker === id;
      marker.classList.toggle("is-active", active);
      marker.setAttribute("aria-pressed", active ? "true" : "false");
      if (active && counter) counter.textContent = String(index + 1).padStart(2, "0");
    });
    tabs.forEach((tab) => {
      const active = tab.dataset.eyeTab === id;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.tabIndex = active ? 0 : -1;
    });
    displays.forEach((display) => {
      const active = display.dataset.eyeDisplay === id;
      display.classList.toggle("is-hidden", !active);
      display.hidden = !active;
    });
  };

  markers.forEach((marker) => marker.addEventListener("click", () => activate(marker.dataset.eyeMarker)));
  tabs.forEach((tab) => {
    tab.addEventListener("click", () => activate(tab.dataset.eyeTab));
    tab.addEventListener("keydown", (event) => {
      const currentIndex = tabs.indexOf(tab);
      const direction = event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
      if (!direction && event.key !== "Home" && event.key !== "End") return;
      event.preventDefault();
      const nextIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : (currentIndex + direction + tabs.length) % tabs.length;
      tabs[nextIndex]?.focus();
      activate(tabs[nextIndex]?.dataset.eyeTab);
    });
  });

  inspector.addEventListener("shopify:block:select", (event) => {
    const marker = event.target.closest?.("[data-eye-marker]");
    if (marker) activate(marker.dataset.eyeMarker);
  });
});

document.querySelectorAll("[data-product-section]").forEach((section) => {
  const select = section.querySelector("[data-variant-select]");
  const variantInput = section.querySelector("[data-variant-id]");
  const variantData = section.querySelector("[data-product-variants]");
  if (!select || !variantData) return;
  let variants;
  try { variants = JSON.parse(variantData.textContent); } catch { return; }

  const syncVariant = (variant, updateUrl = false) => {
    if (!variant) return;
    select.value = String(variant.id);
    if (variantInput) variantInput.value = variant.id;
    const price = section.querySelector("[data-product-price]");
    if (price) {
      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      price.innerHTML = `<span class="jf-price${onSale ? " jf-price--sale" : ""}"><span>${escapeHtml(formatMoney(variant.price))}</span>${onSale ? `<s>${escapeHtml(formatMoney(variant.compare_at_price))}</s>` : ""}</span>`;
    }
    const stickyPrice = section.querySelector(".jf-sticky-buy__price");
    // Vitrina 3.9.11: the sticky bar keeps the cents like the page price ($49,500.00).
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    section.querySelectorAll("[data-add-to-cart], [data-sticky-add-to-cart]").forEach((button) => {
      button.disabled = !variant.available;
      const label = button.querySelector("[data-add-to-cart-text]");
      if (label) label.textContent = variant.available ? window.JaneFindsTheme.addToCart : window.JaneFindsTheme.soldOut;
    });
    section.dispatchEvent(new CustomEvent("janefinds:variant-change", { detail: { variant } }));
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }
  };
  const syncFromUrl = () => {
    const id = new URL(window.location.href).searchParams.get("variant") || select.value;
    syncVariant(variants.find((variant) => String(variant.id) === id));
  };
  select.addEventListener("change", () => syncVariant(variants.find((variant) => String(variant.id) === select.value), true));
  window.addEventListener("popstate", syncFromUrl);
  window.addEventListener("pageshow", syncFromUrl);
  syncFromUrl();
});

document.querySelectorAll("[data-object-record]").forEach((record) => {
  const open = record.querySelector("[data-object-record-open]");
  const dialog = record.querySelector("[data-object-record-dialog]");
  const close = record.querySelector("[data-object-record-close]");
  if (!(dialog instanceof HTMLDialogElement) || !open || !close) return;

  const closeRecord = () => {
    if (dialog.open) dialog.close();
    open.setAttribute("aria-expanded", "false");
    document.body.classList.remove("object-record-open");
    open.focus();
  };

  open.addEventListener("click", () => {
    if (!dialog.open) dialog.showModal();
    open.setAttribute("aria-expanded", "true");
    document.body.classList.add("object-record-open");
  });
  close.addEventListener("click", closeRecord);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) closeRecord();
  });
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeRecord();
  });
  dialog.addEventListener("close", () => {
    open.setAttribute("aria-expanded", "false");
    document.body.classList.remove("object-record-open");
  });
});

document.querySelectorAll("[data-sort-by]").forEach((select) => {
  select.addEventListener("change", () => {
    const url = new URL(window.location.href);
    url.searchParams.set("sort_by", select.value);
    url.searchParams.delete("page");
    window.location.assign(url.toString());
  });
});

document.querySelectorAll("[data-address-country]").forEach((countrySelect) => {
  const provinceSelect = document.getElementById(countrySelect.dataset.provinceTarget || "");
  const provinceContainer = provinceSelect?.closest("[data-province-container]");
  if (!(provinceSelect instanceof HTMLSelectElement)) return;

  const updateProvinces = () => {
    const selectedCountry = countrySelect.options[countrySelect.selectedIndex];
    let provinces = [];
    try {
      provinces = JSON.parse(selectedCountry?.dataset.provinces || "[]");
    } catch {
      provinces = [];
    }

    provinceSelect.replaceChildren();
    provinces.forEach(([value, label]) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      provinceSelect.append(option);
    });

    const defaultProvince = provinceSelect.dataset.default;
    if (defaultProvince && [...provinceSelect.options].some((option) => option.value === defaultProvince)) {
      provinceSelect.value = defaultProvince;
    }
    provinceSelect.disabled = provinces.length === 0;
    provinceContainer?.toggleAttribute("hidden", provinces.length === 0);
  };

  const defaultCountry = countrySelect.dataset.default;
  if (defaultCountry && [...countrySelect.options].some((option) => option.value === defaultCountry)) {
    countrySelect.value = defaultCountry;
  }
  countrySelect.addEventListener("change", updateProvinces);
  updateProvinces();
});

document.querySelectorAll("[data-remove-address]").forEach((form) => {
  form.addEventListener("submit", (event) => {
    if (!window.confirm(form.dataset.confirm || "Remove this address?")) event.preventDefault();
  });
});

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, "text/html");
        const recommendations = html.querySelector("product-recommendations");
        if (recommendations?.innerHTML.trim()) {
          this.innerHTML = recommendations.innerHTML;
          this.querySelectorAll("[data-horizontal-rail]").forEach(initializeHorizontalRail);
          syncWishlistControls();
        }
      })
      .catch(() => {});
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

document.addEventListener("shopify:section:load", () => window.location.reload());

/* Staggered reveal (Vitrine 2.0.2) */
(() => {
  const els = document.querySelectorAll('[data-jf-reveal]');
  if (!els.length) return;
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    els.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px' });
  els.forEach((el) => io.observe(el));
})();


document.querySelectorAll("[data-jf-photos]").forEach((wrap) => {
  const endpoint = (wrap.dataset.uploadEndpoint || "").replace(/\/+$/, "");
  const uploader = wrap.querySelector("[data-jf-uploader]");
  const input = wrap.querySelector("[data-jf-file-input]");
  const list = wrap.querySelector("[data-jf-file-list]");
  const status = wrap.querySelector("[data-jf-upload-status]");
  const links = wrap.querySelector("[data-jf-photo-links]");
  const form = wrap.closest("form");
  if (!endpoint || !uploader || !input || !links) return;
  uploader.hidden = false;
  links.hidden = true;
  let inflight = 0;
  const setBusy = (busy) => {
    const btn = form && form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = busy;
    if (busy) status.textContent = "Uploading photographs…";
  };
  const fallback = (msg) => {
    status.textContent = msg + " You can paste photo links below instead.";
    links.hidden = false;
  };
  input.addEventListener("change", async () => {
    const files = [...input.files].slice(0, 10);
    input.value = "";
    if (!files.length) return;
    const existing = links.value ? links.value.split("\n").filter(Boolean).length : 0;
    if (existing + files.length > 10) {
      status.textContent = "Maximum 10 photographs per submission.";
      return;
    }
    const fd = new FormData();
    let rejected = 0;
    files.forEach((f) => {
      if (f.size > 20 * 1024 * 1024) { rejected += 1; return; }
      fd.append("files", f, f.name);
    });
    if (rejected) status.textContent = rejected + " file(s) over 20 MB were skipped.";
    if (![...fd.keys()].length) return;
    inflight += 1;
    setBusy(true);
    try {
      const res = await fetch(endpoint + "/api/uploads", { method: "POST", body: fd });
      if (!res.ok) throw new Error("upload_failed");
      const data = await res.json();
      (data.urls || []).forEach((url, i) => {
        links.value = (links.value ? links.value + "\n" : "") + url;
        const li = document.createElement("li");
        li.textContent = (files[i] ? files[i].name : "Photograph") + " ✓";
        list.appendChild(li);
      });
      status.textContent = list.children.length + " photograph(s) attached.";
    } catch (e) {
      fallback("Upload didn’t go through.");
    } finally {
      inflight -= 1;
      if (!inflight) setBusy(false);
    }
  });
  if (form) form.addEventListener("submit", (e) => { if (inflight) e.preventDefault(); });
});

